import { useCallback, useEffect, useState } from 'react';
import {
    ClipboardList,
    Inbox,
    Users,
    UserCheck,
    Activity,
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Avatar from '../ui/Avatar';
import { Skeleton } from '../Skeleton';
import {
    getSubmissions,
    listRoleRequests,
    approveRoleRequest,
    rejectRoleRequest,
    listUsers,
    setUserRole,
    assignSubmissionMentor,
    listLearningUpdates,
    runLearningUpdate,
    applyLearningUpdate,
    MODULE_LABELS,
    MODULE_ORDER,
    type Submission,
    type RoleRequest,
    type UserSummary,
    type LearningUpdate,
    type RoleKey,
    type ModuleKey,
} from '../../mentorshipService';
import type { BackendUserWithPlan } from '../../userService';
import NotificationsBell from '../NotificationsBell/NotificationsBell';
import './mentorship.css';

type Tab =
    | 'pending_reviews'
    | 'role_requests'
    | 'manage_users'
    | 'unassigned'
    | 'learning_updates';

type Props = { user: BackendUserWithPlan };

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'pending_reviews', label: 'Pending', icon: <ClipboardList size={16} /> },
    { key: 'unassigned', label: 'Unassigned', icon: <Inbox size={16} /> },
    { key: 'role_requests', label: 'Role requests', icon: <UserCheck size={16} /> },
    { key: 'manage_users', label: 'Manage users', icon: <Users size={16} /> },
    { key: 'learning_updates', label: 'Learning', icon: <Activity size={16} /> },
];

function menteeName(s: Submission): string {
    const m = s.mentee_summary;
    if (!m) return `Mentee #${s.mentee}`;
    return `${m.first_name} ${m.last_name}`.trim() || m.email || `Mentee #${m.id}`;
}

// ─── Pending reviews ──────────────────────────────────────────────────────

function PendingReviewsTab() {
    const [items, setItems] = useState<Submission[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const page = await getSubmissions();
                if (!cancelled) setItems(page.results.filter((s) => s.state !== 'finalized'));
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) return <Skeleton width="100%" height="80px" borderRadius="12px" />;
    if (!items || items.length === 0)
        return (
            <div className="empty-state">
                <h3>Nothing pending</h3>
                <p>All submissions are finalized.</p>
            </div>
        );

    return (
        <ul className="admin-list">
            {items.map((s) => (
                <li key={s.id}>
                    <Card variant="row">
                        <Card.Header>
                            <Avatar name={menteeName(s)} size="md" />
                            <div>
                                <p><strong>{menteeName(s)}</strong></p>
                                <p>
                                    {MODULE_LABELS[s.module]} · {s.quarter} · {s.state}
                                </p>
                                <p>
                                    Mentor:{' '}
                                    {s.assigned_mentor
                                        ? `#${s.assigned_mentor}`
                                        : 'unassigned'}
                                </p>
                            </div>
                        </Card.Header>
                    </Card>
                </li>
            ))}
        </ul>
    );
}

// ─── Unassigned ───────────────────────────────────────────────────────────

function UnassignedTab() {
    const [items, setItems] = useState<Submission[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState<Submission | null>(null);
    const [mentorId, setMentorId] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await getSubmissions();
            setItems(page.results.filter((s) => !s.assigned_mentor));
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const assign = async () => {
        if (!active || !mentorId.trim()) return;
        setSaving(true);
        try {
            await assignSubmissionMentor(active.id, Number(mentorId), reason);
            setActive(null);
            setMentorId('');
            setReason('');
            await load();
        } catch (err) {
            console.error('[UnassignedTab] assign failed', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Skeleton width="100%" height="80px" borderRadius="12px" />;
    if (!items || items.length === 0)
        return (
            <div className="empty-state">
                <h3>No unassigned submissions</h3>
                <p>All submissions have a mentor.</p>
            </div>
        );

    return (
        <>
            <ul className="admin-list">
                {items.map((s) => (
                    <li key={s.id}>
                        <Card variant="row" interactive onClick={() => setActive(s)}>
                            <Card.Header>
                                <Avatar name={menteeName(s)} size="md" />
                                <div>
                                    <p><strong>{menteeName(s)}</strong></p>
                                    <p>{MODULE_LABELS[s.module]} · {s.quarter}</p>
                                </div>
                            </Card.Header>
                        </Card>
                    </li>
                ))}
            </ul>
            {active && (
                <Modal
                    open
                    onClose={() => setActive(null)}
                    size="md"
                    ariaLabel="Assign mentor"
                >
                    <Modal.Header onClose={() => setActive(null)}>
                        Assign mentor
                    </Modal.Header>
                    <Modal.Body>
                        <p>{menteeName(active)} · {MODULE_LABELS[active.module]}</p>
                        <label htmlFor="assign-mentor-id">Mentor user id</label>
                        <input
                            id="assign-mentor-id"
                            type="number"
                            value={mentorId}
                            onChange={(e) => setMentorId(e.target.value)}
                        />
                        <label htmlFor="assign-reason">Reason</label>
                        <input
                            id="assign-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="ghost" size="md" onClick={() => setActive(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={assign}
                            disabled={saving || !mentorId.trim()}
                        >
                            {saving ? 'Assigning…' : 'Assign'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
}

// ─── Role requests ────────────────────────────────────────────────────────

function RoleRequestsTab() {
    const [items, setItems] = useState<RoleRequest[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [actingId, setActingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listRoleRequests('pending');
            setItems(page.results);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const approve = async (r: RoleRequest) => {
        setActingId(r.id);
        try {
            await approveRoleRequest(r.id);
            await load();
        } finally {
            setActingId(null);
        }
    };

    const reject = async (r: RoleRequest) => {
        const reason = window.prompt('Reason for rejection?') ?? '';
        if (!reason) return;
        setActingId(r.id);
        try {
            await rejectRoleRequest(r.id, reason);
            await load();
        } finally {
            setActingId(null);
        }
    };

    if (loading) return <Skeleton width="100%" height="80px" borderRadius="12px" />;
    if (!items || items.length === 0)
        return (
            <div className="empty-state">
                <h3>No pending role requests</h3>
                <p>You're caught up.</p>
            </div>
        );

    return (
        <ul className="admin-list">
            {items.map((r) => (
                <li key={r.id}>
                    <Card variant="row">
                        <Card.Header>
                            <div>
                                <p><strong>User #{r.user}</strong></p>
                                <p>{r.request_type}</p>
                                <pre className="admin-payload">
                                    {JSON.stringify(r.payload, null, 2)}
                                </pre>
                            </div>
                            <div className="admin-actions">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => approve(r)}
                                    disabled={actingId === r.id}
                                >
                                    Approve
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => reject(r)}
                                    disabled={actingId === r.id}
                                >
                                    Reject
                                </Button>
                            </div>
                        </Card.Header>
                    </Card>
                </li>
            ))}
        </ul>
    );
}

// ─── Manage users (RoleManagementTable + RoleChangeDrawer) ────────────────

function RoleChangeDrawer({
    user,
    onClose,
    onSaved,
}: {
    user: UserSummary;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [newRole, setNewRole] = useState<'mentor' | 'mentee' | null>(
        user.role === 'mentor' || user.role === 'mentee' ? user.role : 'mentee',
    );
    const [reason, setReason] = useState('');
    const [specialties, setSpecialties] = useState<ModuleKey[]>(user.mentor_specialties);
    const [capacity, setCapacity] = useState<string>(String(user.mentor_capacity ?? 10));
    const [primaryMentor, setPrimaryMentor] = useState<string>(
        user.mentor_id ? String(user.mentor_id) : '',
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleSpecialty = (m: ModuleKey) => {
        setSpecialties((prev) =>
            prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
        );
    };

    const submit = async () => {
        if (!reason.trim()) {
            setError('Reason is required.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await setUserRole(user.id, {
                new_role: newRole,
                reason: reason.trim(),
                mentor_id: newRole === 'mentee' && primaryMentor ? Number(primaryMentor) : null,
                mentor_specialties: newRole === 'mentor' ? specialties : undefined,
                mentor_capacity:
                    newRole === 'mentor' && capacity ? Number(capacity) : undefined,
            });
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            size="md"
            placement="right"
            ariaLabel="Change role"
        >
            <Modal.Header onClose={onClose}>
                Change role · {user.first_name} {user.last_name}
            </Modal.Header>
            <Modal.Body>
                <p className="role-drawer__current">Current: {user.role ?? 'none'}</p>

                <fieldset className="role-drawer__fieldset">
                    <legend>New role</legend>
                    {(['mentor', 'mentee', null] as const).map((r) => (
                        <label key={String(r)}>
                            <input
                                type="radio"
                                name="new-role"
                                checked={newRole === r}
                                onChange={() => setNewRole(r)}
                            />{' '}
                            {r ?? 'Remove role'}
                        </label>
                    ))}
                </fieldset>

                {newRole === 'mentor' && (
                    <>
                        <label className="role-drawer__label">Specialties</label>
                        <div className="role-drawer__specialties">
                            {MODULE_ORDER.map((m) => {
                                const checked = specialties.includes(m);
                                return (
                                    <button
                                        key={m}
                                        type="button"
                                        className={`mentorship-chip mentorship-chip--module ${checked ? 'is-active' : ''}`}
                                        onClick={() => toggleSpecialty(m)}
                                        aria-pressed={checked ? 'true' : 'false'}
                                    >
                                        {MODULE_LABELS[m]}
                                    </button>
                                );
                            })}
                        </div>

                        <label htmlFor="rd-capacity" className="role-drawer__label">
                            Capacity
                        </label>
                        <input
                            id="rd-capacity"
                            type="number"
                            value={capacity}
                            onChange={(e) => setCapacity(e.target.value)}
                        />
                    </>
                )}

                {newRole === 'mentee' && (
                    <>
                        <label htmlFor="rd-mentor" className="role-drawer__label">
                            Primary mentor user id (optional)
                        </label>
                        <input
                            id="rd-mentor"
                            type="number"
                            value={primaryMentor}
                            onChange={(e) => setPrimaryMentor(e.target.value)}
                        />
                    </>
                )}

                <label htmlFor="rd-reason" className="role-drawer__label">
                    Reason (required)
                </label>
                <textarea
                    id="rd-reason"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />

                {error && <p className="report-submit__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" size="md" onClick={submit} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function ManageUsersTab() {
    const [items, setItems] = useState<UserSummary[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState<RoleKey | ''>('');
    const [active, setActive] = useState<UserSummary | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listUsers({
                search: search.trim() || undefined,
                role: roleFilter || undefined,
            });
            setItems(page.results);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [search, roleFilter]);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <div>
            <div className="admin-toolbar">
                <input
                    type="search"
                    aria-label="Search users"
                    placeholder="Search users…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select
                    aria-label="Filter by role"
                    value={roleFilter ?? ''}
                    onChange={(e) =>
                        setRoleFilter(
                            (e.target.value as RoleKey | '') === ''
                                ? ''
                                : (e.target.value as RoleKey),
                        )
                    }
                >
                    <option value="">All roles</option>
                    <option value="mentor">Mentor</option>
                    <option value="mentee">Mentee</option>
                    <option value="admin">Admin</option>
                </select>
            </div>
            {loading ? (
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            ) : !items || items.length === 0 ? (
                <div className="empty-state">
                    <h3>No users match</h3>
                </div>
            ) : (
                <ul className="admin-list">
                    {items.map((u) => (
                        <li key={u.id}>
                            <Card
                                variant="row"
                                interactive
                                onClick={() => setActive(u)}
                            >
                                <Card.Header>
                                    <Avatar
                                        name={`${u.first_name} ${u.last_name}`.trim() || u.email}
                                        size="md"
                                    />
                                    <div>
                                        <p>
                                            <strong>
                                                {`${u.first_name} ${u.last_name}`.trim() || u.username}
                                            </strong>
                                        </p>
                                        <p>{u.email}</p>
                                        <span className="mentorship-chip mentorship-chip--module">
                                            {u.role ?? 'no role'}
                                        </span>
                                        {u.role === 'mentor' && (
                                            <span className="mentorship-chip mentorship-chip--ready">
                                                load {u.active_load}/{u.mentor_capacity ?? 0}
                                            </span>
                                        )}
                                    </div>
                                </Card.Header>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}
            {active && (
                <RoleChangeDrawer
                    user={active}
                    onClose={() => setActive(null)}
                    onSaved={() => {
                        setActive(null);
                        void load();
                    }}
                />
            )}
        </div>
    );
}

// ─── Learning updates ─────────────────────────────────────────────────────

function LearningUpdatesTab() {
    const [items, setItems] = useState<LearningUpdate[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listLearningUpdates();
            setItems(page.results);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const run = async () => {
        setRunning(true);
        try {
            await runLearningUpdate();
            await load();
        } finally {
            setRunning(false);
        }
    };

    const apply = async (u: LearningUpdate) => {
        try {
            await applyLearningUpdate(u.id);
            await load();
        } catch (err) {
            console.error('[LearningUpdatesTab] apply failed', err);
        }
    };

    return (
        <div>
            <div className="mentor-projects__header">
                <h3>Learning updates</h3>
                <Button
                    variant="primary"
                    size="md"
                    onClick={run}
                    disabled={running}
                >
                    {running ? 'Running…' : 'Run now'}
                </Button>
            </div>
            {loading ? (
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            ) : !items || items.length === 0 ? (
                <div className="empty-state">
                    <h3>No learning updates yet</h3>
                    <p>Click "Run now" to detect patterns across all mentees.</p>
                </div>
            ) : (
                <ul className="admin-list">
                    {items.map((u) => (
                        <li key={u.id}>
                            <Card variant="stacked">
                                <Card.Header>
                                    <p>
                                        <strong>
                                            {new Date(u.generated_at).toLocaleString()}
                                        </strong>
                                    </p>
                                    {!u.applied_at && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => apply(u)}
                                        >
                                            Mark applied
                                        </Button>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {u.patterns_detected.length > 0 && (
                                        <>
                                            <h4>Patterns</h4>
                                            <ul>
                                                {u.patterns_detected.map((p, i) => (
                                                    <li key={i}>
                                                        {p.pattern} ({p.evidence_count})
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    {u.system_recommendations.length > 0 && (
                                        <>
                                            <h4>Recommendations</h4>
                                            <ul>
                                                {u.system_recommendations.map((r, i) => (
                                                    <li key={i}>{r}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ─── Shell ────────────────────────────────────────────────────────────────

function AdminMentorshipView({ user }: Props) {
    const [activeTab, setActiveTab] = useState<Tab>('pending_reviews');

    return (
        <section className="mentorship-view">
            <header className="mentorship-view__header">
                <div>
                    <h2 className="mentorship-view__title">
                        Coaching · Admin · {user.first_name || user.username}
                    </h2>
                    <p className="mentorship-view__subtitle">
                        Platform-wide mentorship operations.
                    </p>
                </div>
                <NotificationsBell />
            </header>

            <nav
                className="mentorship-tabs"
                role="tablist"
                aria-label="Admin dashboard tabs"
            >
                {TABS.map((t) => {
                    const isActive = activeTab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive ? 'true' : 'false'}
                            className={`mentorship-tab ${isActive ? 'is-active' : ''}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            {t.icon} {t.label}
                        </button>
                    );
                })}
            </nav>

            <div className="mentorship-view__content">
                {activeTab === 'pending_reviews' && <PendingReviewsTab />}
                {activeTab === 'unassigned' && <UnassignedTab />}
                {activeTab === 'role_requests' && <RoleRequestsTab />}
                {activeTab === 'manage_users' && <ManageUsersTab />}
                {activeTab === 'learning_updates' && <LearningUpdatesTab />}
            </div>
        </section>
    );
}

export default AdminMentorshipView;
